/**
 * Public renderer for rich text. Walks the stored document with an allowlist and builds
 * React elements; unknown nodes are skipped. No Tiptap, no dangerouslySetInnerHTML.
 */
import { Fragment, useState, type ReactNode } from "react";
import { Play } from "lucide-react";
import { LocaleLink, type PublicPath } from "@/components/site/LocaleLink";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { useLang } from "@/i18n/use-lang";
import { vulTokens } from "@/lib/page-content";
import { hrefToegelaten, leesRijkeTekst, youtubeId, type RtMark, type RtNode } from "@/lib/rich-text";

type Tone = "licht" | "donker" | "erf";

const STATISCHE_PADEN = new Set<string>([
  "/",
  "/nieuwbouw",
  "/renovatie",
  "/technieken",
  "/warmtepompen-afdekkingen",
  "/realisaties",
  "/opbouw-zwembad",
  "/over-ons",
  "/offerte",
  "/blog",
]);

function linkClass(tone: Tone) {
  if (tone === "erf") {
    return "font-semibold underline underline-offset-4 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-current";
  }
  return tone === "donker"
    ? "font-semibold text-brand-cyan underline underline-offset-4 hover:text-brand-cyan-dark focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-cyan"
    : "font-semibold text-brand-deep-water underline underline-offset-4 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-deep-water";
}

function RichLink({ href, tone, children }: { href: string; tone: Tone; children: ReactNode }) {
  const lang = useLang();
  const doel = vulTokens(href).trim();
  if (!hrefToegelaten(doel)) return <>{children}</>;
  if (doel.startsWith("/")) {
    if (STATISCHE_PADEN.has(doel)) {
      return (
        <LocaleLink to={doel as PublicPath} className={linkClass(tone)}>
          {children}
        </LocaleLink>
      );
    }
    return (
      <a href={`/${lang}${doel}`} className={linkClass(tone)}>
        {children}
      </a>
    );
  }
  const extern = doel.startsWith("https://");
  return (
    <a
      href={doel}
      className={linkClass(tone)}
      {...(extern ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

function withMarks(text: ReactNode, marks: RtMark[] | undefined, tone: Tone): ReactNode {
  let out = text;
  for (const mark of marks ?? []) {
    if (mark.type === "bold") out = <strong className="font-semibold">{out}</strong>;
    else if (mark.type === "italic") out = <em>{out}</em>;
    else if (mark.type === "superscript") out = <sup>{out}</sup>;
    else if (mark.type === "subscript") out = <sub>{out}</sub>;
    else if (mark.type === "link") {
      out = (
        <RichLink href={String(mark.attrs?.["href"] ?? "")} tone={tone}>
          {out}
        </RichLink>
      );
    }
  }
  return out;
}

function RichYoutube({ id, title }: { id: string; title: string }) {
  const [actief, setActief] = useState(false);
  return (
    <figure className="my-8 aspect-[16/9] w-full overflow-hidden rounded-lg bg-brand-navy">
      {actief ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1`}
          title={title}
          allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className="h-full w-full"
        />
      ) : (
        <button
          type="button"
          onClick={() => setActief(true)}
          className="group relative h-full w-full focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-cyan"
        >
          <img
            src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
            alt=""
            width={480}
            height={360}
            loading="lazy"
            className="h-full w-full object-cover opacity-80"
          />
          <span className="absolute inset-0 flex items-center justify-center gap-3 bg-brand-navy/40 text-primary-foreground">
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-primary-foreground/60 bg-brand-navy/80">
              <Play className="h-6 w-6" strokeWidth={1.75} aria-hidden="true" />
            </span>
            <span className="text-base font-semibold">{title}</span>
          </span>
        </button>
      )}
    </figure>
  );
}

function renderNodes(nodes: RtNode[] | undefined, tone: Tone): ReactNode[] {
  return (nodes ?? []).map((node, index) => <Fragment key={index}>{renderNode(node, tone)}</Fragment>);
}

function renderNode(node: RtNode, tone: Tone): ReactNode {
  const kinderen = () => renderNodes(node.content, tone);
  switch (node.type) {
    case "text":
      return withMarks(vulTokens(node.text ?? ""), node.marks, tone);
    case "hardBreak":
      return <br />;
    case "paragraph":
      return <p>{kinderen()}</p>;
    case "heading":
      return <h3 className="pt-4 font-display text-xl font-semibold leading-7 text-foreground">{kinderen()}</h3>;
    case "bulletList":
      return <ul className="list-disc space-y-2 pl-6">{kinderen()}</ul>;
    case "orderedList":
      return <ol className="list-decimal space-y-2 pl-6">{kinderen()}</ol>;
    case "listItem":
      return <li className="[&>p]:m-0">{kinderen()}</li>;
    case "blockquote":
      return <blockquote className="border-l border-border pl-5 italic">{kinderen()}</blockquote>;
    case "horizontalRule":
      return <hr className="my-8 border-border" />;
    case "table": {
      const rijen = node.content ?? [];
      const kop = rijen[0]?.content?.every((cel) => cel.type === "tableHeader") ? rijen[0] : null;
      const body = kop ? rijen.slice(1) : rijen;
      return (
        <div className="overflow-x-auto border-y border-border">
          <table className="w-full min-w-[520px] border-collapse text-left text-sm">
            {kop && (
              <thead>
                <tr>
                  {(kop.content ?? []).map((cel, i) => (
                    <th key={i} scope="col" className="border-b border-border px-4 py-3 font-semibold text-foreground [&>p]:m-0">
                      {renderNodes(cel.content, tone)}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {body.map((rij, r) => (
                <tr key={r} className="border-t border-border">
                  {(rij.content ?? []).map((cel, c) => (
                    <td key={c} className="px-4 py-3 align-top [&>p]:m-0">
                      {renderNodes(cel.content, tone)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case "image": {
      const src = String(node.attrs?.["src"] ?? "");
      const alt = vulTokens(String(node.attrs?.["alt"] ?? ""));
      if (!src || !alt) return null;
      const caption = vulTokens(String(node.attrs?.["caption"] ?? ""));
      return (
        <figure className="my-8">
          <div className="overflow-hidden rounded-lg">
            <ResponsiveImage src={src} alt={alt} ratio="16:9" width={1600} height={900} sizes="(min-width: 768px) 720px, 100vw" />
          </div>
          {caption && <figcaption className="mt-2 text-sm">{caption}</figcaption>}
        </figure>
      );
    }
    case "youtube": {
      const id = youtubeId(node.attrs?.["src"]);
      const title = String(node.attrs?.["title"] ?? "");
      return id && title ? <RichYoutube id={id} title={title} /> : null;
    }
    case "details":
      return (
        <details className="group border-y border-border py-4">
          {kinderen()}
        </details>
      );
    case "detailsSummary":
      return (
        <summary className="cursor-pointer font-semibold text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-deep-water">
          {kinderen()}
        </summary>
      );
    case "detailsContent":
      return <div className="mt-3 space-y-4">{kinderen()}</div>;
    default:
      return null;
  }
}

/** Inline rendering: paragraphs separated by line breaks, for use inside an existing <p>. */
function renderInline(nodes: RtNode[], tone: Tone): ReactNode[] {
  const uit: ReactNode[] = [];
  const loop = (node: RtNode) => {
    if (node.type === "text" || node.type === "hardBreak") {
      uit.push(<Fragment key={uit.length}>{renderNode(node, tone)}</Fragment>);
      return;
    }
    const blok = node.type === "paragraph" || node.type === "listItem" || node.type === "heading";
    if (blok && uit.length > 0) uit.push(<br key={uit.length} />);
    if (node.type === "listItem") uit.push(<Fragment key={uit.length}>• </Fragment>);
    (node.content ?? []).forEach(loop);
  };
  nodes.forEach(loop);
  return uit;
}

type RichTextProps = {
  waarde: string;
  tone?: "licht" | "donker";
  /** Render without block wrappers, for use inside an existing paragraph or string slot. */
  inline?: boolean;
  className?: string;
};

export function RichText({ waarde, tone = "licht", inline = false, className = "" }: RichTextProps) {
  const doc = leesRijkeTekst(waarde);
  if (inline) return <>{renderInline(doc.content ?? [], "erf")}</>;
  return <div className={`space-y-4 ${className}`.trim()}>{renderNodes(doc.content, tone)}</div>;
}
